import { useEffect, useState } from 'react';
import { Card, SectionTitle, ErrorBanner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  active: boolean;
  createdAt: string;
}

export function AdminUsersPage() {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load(query = '') {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<{ users: AdminUser[] }>(
        `/admin/users${query ? `?q=${encodeURIComponent(query)}` : ''}`,
      );
      setUsers(res.users);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to load users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function act(fn: () => Promise<void>) {
    try {
      await fn();
      load(q);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Action failed.');
    }
  }

  return (
    <div className="space-y-4">
      <SectionTitle>Users</SectionTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load(q);
        }}
        className="flex gap-2"
      >
        <input
          className="input"
          placeholder="Search by name or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button className="btn-primary" type="submit">
          Search
        </button>
      </form>

      {error && <ErrorBanner message={error} />}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Joined</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center text-muted">
                    Loading…
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="border-b border-line last:border-0">
                    <td className="px-2 py-2 font-medium">{u.name}</td>
                    <td className="px-2 py-2 text-muted">{u.email}</td>
                    <td className="px-2 py-2">
                      {u.active ? (
                        <span className="chip bg-success/15 text-success">Active</span>
                      ) : (
                        <span className="chip bg-danger/10 text-danger">Disabled</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-muted">{formatDateTime(u.createdAt)}</td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          className={u.active ? 'text-danger underline' : 'text-success underline'}
                          onClick={() =>
                            act(() => api.post(`/admin/users/${u.id}/active`, { active: !u.active }))
                          }
                        >
                          {u.active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
